import { ChangeEvent, FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { useTranslation } from 'react-i18next';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Stack,
  TextFieldProps,
  Tooltip,
  useTheme,
} from '@mui/material';

import { useSnackbar } from 'notistack';

import { ReactComponent as FormInfo } from '@/assets/icons/form-info-icon.svg';
import { REGEX_NOT_DIGITS_AND_DOT } from '@/shared/constants';
import { camelToSnakeCase } from '@/shared/lib/strings';
import {
  COMPACT_FIELD_WIDTH,
  COMPACT_FORM_LABEL_SX,
  buildCompactInputSx,
} from '@/shared/ui/form-fields/fieldStyles';
import { FormError, FormLabel } from '@/shared/ui/styled';

type Props = {
  name: string;
  isRequired?: boolean;
  label?: string;
  extraLabel?: string;
  tooltip?: string;
  valueType?: 'string' | 'number' | 'float' | 'price';
  validation?: string | RegExp;
  validateOnChange?: boolean;
  onChange?: (value: string | number | undefined) => void;
  fullWidth?: boolean;
} & Omit<TextFieldProps, 'onChange' | 'fullWidth'>;

const useDebouncedValidation = (trigger: (name: string) => void, delay = 300) => {
  const timeoutRef = useRef<number | null>(null);

  return useCallback(
    (fieldName: string) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        trigger(fieldName);
      }, delay);
    },
    [trigger, delay],
  );
};

const RhfCompactTextField: FC<Props> = ({
  name,
  isRequired,
  label,
  extraLabel,
  tooltip,
  disabled,
  valueType,
  validation,
  validateOnChange = false,
  fullWidth = false,
  ...muiProps
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { trigger } = useFormContext();

  const debouncedValidate = useDebouncedValidation(trigger, 300);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [inputDisplay, setInputDisplay] = useState<string | null>(null);

  const inputElRef = useRef<HTMLInputElement | null>(null);
  const pendingCursorRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (pendingCursorRef.current !== null && inputElRef.current) {
      inputElRef.current.setSelectionRange(pendingCursorRef.current, pendingCursorRef.current);
      pendingCursorRef.current = null;
    }
  });

  const isNumericType = valueType === 'number' || valueType === 'float' || valueType === 'price';

  const type = useMemo(() => {
    if (muiProps.type === 'password') {
      return showPassword ? 'text' : 'password';
    }

    return muiProps.type;
  }, [muiProps.type, showPassword]);

  const iconStyles = { fontSize: 17, color: theme.palette.text.primary, marginRight: 5 };

  const formatNumericValue = useCallback((value: string | number | undefined): string => {
    if (typeof value === 'number') {
      return value.toLocaleString('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
    if (typeof value === 'string' && value !== '' && value !== '-') {
      const parsed = parseFloat(value.replaceAll('.', '').replaceAll(',', '.'));
      if (!isNaN(parsed)) {
        return parsed.toLocaleString('ro-RO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
      }
    }
    return '';
  }, []);

  const getValue = useCallback(
    (value: string | number | undefined) => {
      if (isNumericType) {
        if (inputDisplay !== null) return inputDisplay;
        return formatNumericValue(value);
      }
      return value ?? '';
    },
    [isNumericType, inputDisplay, formatNumericValue],
  );

  const formatLiveNumeric = useCallback((raw: string): string => {
    if (raw === '' || raw === '-') return raw;
    const commaIdx = raw.lastIndexOf(',');
    const intPart = commaIdx >= 0 ? raw.slice(0, commaIdx) : raw;
    const decPart = commaIdx >= 0 ? raw.slice(commaIdx) : '';
    const sign = intPart.startsWith('-') ? '-' : '';
    const digits = intPart.replace(/[^0-9]/g, '');
    if (digits === '') return sign + decPart;
    const formatted = parseInt(digits, 10).toLocaleString('ro-RO');
    return sign + formatted + decPart;
  }, []);

  const handleNumericFocus = useCallback(
    (value: string | number | undefined) => {
      if (!isNumericType) return;
      if (typeof value === 'number') {
        setInputDisplay(value === 0 ? '' : formatNumericValue(value));
      } else {
        setInputDisplay(value ? formatLiveNumeric(value) : '');
      }
    },
    [isNumericType, formatNumericValue, formatLiveNumeric],
  );

  const handleNumericBlur = useCallback(() => {
    if (isNumericType) setInputDisplay(null);
  }, [isNumericType]);

  const onChange = useCallback(
    (field: ControllerRenderProps, e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (validation && !RegExp(validation).test(e.target.value)) {
        return;
      }

      let newValue;
      if (!valueType || valueType === 'string') {
        newValue = e.target.value ?? '';
      } else if (valueType === 'number') {
        if (!/^-?[\d.]*$/.test(e.target.value)) {
          return;
        }

        const rawDigits = e.target.value.replace(/[^0-9]/g, '');
        const prevDigits = (inputDisplay ?? '').replace(/[^0-9]/g, '');
        if (rawDigits.length > 15 && rawDigits.length > prevDigits.length) return;

        newValue =
          e.target.value === '' || e.target.value === '-'
            ? e.target.value
            : parseInt(e.target.value.replaceAll(',', '').replaceAll('.', ''), 10);

        if (typeof newValue === 'number' && !isNaN(newValue)) {
          if (newValue < (muiProps?.inputProps?.min ?? 0)) {
            enqueueSnackbar(
              `${t('Number must be equal or greater than')} ${muiProps.inputProps?.min ?? 0}`,
            );
            return;
          }
          if (muiProps.inputProps?.max !== undefined && newValue > muiProps.inputProps.max) {
            enqueueSnackbar(`${t('Number must be equal or less than')} ${muiProps.inputProps.max}`);
            return;
          }
        }
      } else if (valueType === 'float') {
        if (!/^-?[\d,.]*$/.test(e.target.value)) {
          return;
        }

        const rawFloatDigits = e.target.value.split(',')[0].replace(/[^0-9]/g, '');
        const prevFloatDigits = (inputDisplay ?? '').split(',')[0].replace(/[^0-9]/g, '');
        if (rawFloatDigits.length > 13 && rawFloatDigits.length > prevFloatDigits.length) return;

        const floatDecimal = e.target.value.split(',')[1] ?? '';
        const prevFloatDecimal = (inputDisplay ?? '').split(',')[1] ?? '';
        if (floatDecimal.length > 2 && floatDecimal.length > prevFloatDecimal.length) return;

        newValue =
          e.target.value === '' ||
          e.target.value === '-' ||
          e.target.value[e.target.value.length - 1] === ',' ||
          (e.target.value.includes(',') && e.target.value[e.target.value.length - 1] === '0')
            ? e.target.value
            : parseFloat(e.target.value.replaceAll('.', '').replaceAll(',', '.'));

        const parsedForValidation = parseFloat(
          e.target.value.replace(/,$/, '').replaceAll('.', '').replaceAll(',', '.'),
        );
        if (!isNaN(parsedForValidation)) {
          if (
            muiProps?.inputProps?.min !== undefined &&
            parsedForValidation < muiProps.inputProps.min
          ) {
            enqueueSnackbar(
              `${t('Number must be equal or greater than')} ${muiProps.inputProps.min}`,
            );
            return;
          }
          if (
            muiProps.inputProps?.max !== undefined &&
            parsedForValidation > muiProps.inputProps.max
          ) {
            enqueueSnackbar(`${t('Number must be equal or less than')} ${muiProps.inputProps.max}`);
            return;
          }
        }
      } else if (valueType === 'price') {
        if (!/^-?[\d,.]*$/.test(e.target.value)) {
          return;
        }

        const rawPriceDigits = e.target.value.split(',')[0].replace(/[^0-9]/g, '');
        const prevPriceDigits = (inputDisplay ?? '').split(',')[0].replace(/[^0-9]/g, '');
        if (rawPriceDigits.length > 13 && rawPriceDigits.length > prevPriceDigits.length) return;

        const priceDecimal = e.target.value.split(',')[1] ?? '';
        const prevPriceDecimal = (inputDisplay ?? '').split(',')[1] ?? '';
        if (priceDecimal.length > 2 && priceDecimal.length > prevPriceDecimal.length) return;

        newValue =
          e.target.value === '' ||
          e.target.value === '-' ||
          e.target.value[e.target.value.length - 1] === ',' ||
          (e.target.value.includes(',') && e.target.value[e.target.value.length - 1] === '0')
            ? e.target.value
            : parseFloat(e.target.value.replaceAll('.', '').replaceAll(',', '.'));
      } else {
        newValue = e.target.value.replaceAll(REGEX_NOT_DIGITS_AND_DOT, '');
      }

      if (isNumericType) {
        const oldCursor = e.target.selectionStart ?? 0;
        const nonDotsBefore = e.target.value.slice(0, oldCursor).replace(/\./g, '').length;
        const newDisplay = formatLiveNumeric(e.target.value);
        let cursor = newDisplay.length;
        let count = 0;
        for (let i = 0; i < newDisplay.length; i++) {
          if (count === nonDotsBefore) {
            cursor = i;
            break;
          }
          if (newDisplay[i] !== '.') count++;
        }
        pendingCursorRef.current = cursor;
        setInputDisplay(newDisplay);
      }
      field.onChange(newValue);
      muiProps.onChange?.(newValue);

      if (validateOnChange) {
        debouncedValidate(name);
      }
    },
    [
      validation,
      valueType,
      isNumericType,
      formatLiveNumeric,
      muiProps,
      enqueueSnackbar,
      t,
      validateOnChange,
      debouncedValidate,
      name,
      inputDisplay,
    ],
  );

  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error }, formState: { isSubmitting } }) => (
        <FormControl error={!!error} sx={{ width: fullWidth ? '100%' : COMPACT_FIELD_WIDTH }}>
          {!!label && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormLabel sx={COMPACT_FORM_LABEL_SX}>
                {label}{' '}
                {!!extraLabel && <span style={{ textTransform: 'none' }}>{extraLabel}</span>}
              </FormLabel>

              {!!tooltip && (
                <Tooltip title={tooltip}>
                  <span>
                    <Button type="button" sx={{ minWidth: 0, padding: '0.25rem' }} disableRipple>
                      <FormInfo />
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}

          <MuiTextField
            {...field}
            {...muiProps}
            inputRef={inputElRef}
            placeholder={
              muiProps.placeholder
                ? muiProps.placeholder
                : isRequired
                  ? t('required')
                  : t('optional')
            }
            label={undefined}
            type={type}
            value={getValue(field.value)}
            onFocus={() => handleNumericFocus(field.value)}
            onBlur={() => {
              handleNumericBlur();
              field.onBlur();
            }}
            onChange={(e) => onChange(field, e)}
            disabled={isSubmitting || disabled}
            error={Boolean(error)}
            sx={{
              ...buildCompactInputSx(!!error, {
                ...muiProps.sx,
                '& .MuiInputAdornment-positionEnd': { marginRight: '0.5rem !important' },
                ...(muiProps.multiline ? { '& .MuiOutlinedInput-root': { height: 'auto' } } : {}),
              }),
            }}
            autoComplete="off"
            inputProps={{
              value: getValue(field.value),
              id: `${camelToSnakeCase(name)}-input`,
              ...muiProps.inputProps,
              type: 'text',
            }}
            InputProps={{
              ...muiProps.InputProps,
              endAdornment:
                muiProps.type === 'password' ? (
                  <InputAdornment position="end">
                    <IconButton
                      style={{ backgroundColor: 'transparent' }}
                      onClick={() => setShowPassword((prevState) => !prevState)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOff style={iconStyles} />
                      ) : (
                        <Visibility style={iconStyles} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ) : (
                  muiProps.InputProps?.endAdornment || null
                ),
            }}
          />

          {!!error && (
            <FormError>
              {error?.type === 'invalid_type' ? t('errors.required_field') : error.message}
            </FormError>
          )}
        </FormControl>
      )}
    />
  );
};

export default RhfCompactTextField;
